// Device tokens manager. Lists the current user's tokens (id, name,
// createdAt, lastUsedAt) and lets them mint a new one or revoke an
// existing one.
//
// Newly-minted token strings are shown ONCE — once the user clicks
// past the "I've saved this" gate, the raw token is gone from local
// state and there's no way to retrieve it. They'd have to revoke +
// re-issue.

import { useEffect, useState } from "preact/hooks";
import { Icon } from "../../icons";
import { confirm } from "../../components/confirm";
import { api, type DeviceToken } from "../../lib/rpc";

interface DeviceTokensModalProps {
  open: boolean;
  onClose: () => void;
  notify: (msg: string) => void;
}

export function DeviceTokensModal({ open, onClose, notify }: DeviceTokensModalProps) {
  const [tokens, setTokens] = useState<DeviceToken[] | null>(null);
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);
  // Stored only after a successful issue, until the user acknowledges.
  const [freshToken, setFreshToken] = useState<{ id: string; name: string; token: string } | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setNewName("");
    setFreshToken(null);
    api.deviceTokenList()
      .then((t) => { if (!cancelled) setTokens(t); })
      .catch(() => { if (!cancelled) setTokens([]); });
    return () => { cancelled = true; };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !freshToken && !busy) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose, freshToken, busy]);

  if (!open) return null;

  const issue = async () => {
    const name = newName.trim();
    if (!name) { notify("Give the token a name"); return; }
    setBusy(true);
    try {
      const r = await api.deviceTokenIssue(name);
      setFreshToken({ id: r.id, name: r.name, token: r.token });
      // Refresh the list so the new (id, name, createdAt) row shows
      // alongside its one-time token panel.
      const list = await api.deviceTokenList().catch(() => null);
      if (list) setTokens(list);
      setNewName("");
    } catch (e: any) {
      notify(e?.message || "Couldn't issue token");
    } finally { setBusy(false); }
  };

  const revoke = async (tok: DeviceToken) => {
    const ok = await confirm({
      title: `Revoke "${tok.name}"?`,
      message: "Scripts and devices using this token will lose access immediately.",
      confirmLabel: "Revoke",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.deviceTokenRevoke(tok.id);
      setTokens((prev) => (prev ? prev.filter((t) => t.id !== tok.id) : prev));
      notify("Token revoked");
    } catch (e: any) {
      notify(e?.message || "Revoke failed");
    }
  };

  const copyFresh = async () => {
    if (!freshToken) return;
    try { await navigator.clipboard.writeText(freshToken.token); notify("Token copied"); }
    catch { notify("Couldn't copy — select the token manually"); }
  };

  return (
    <div className="settings-submodal-bg" onClick={(e) => { if (e.target === e.currentTarget && !freshToken && !busy) onClose(); }}>
      <div className="settings-submodal" onClick={(e) => e.stopPropagation()}>
        <header className="settings-submodal-head">
          <h3 className="settings-submodal-title">Device tokens</h3>
          <button className="icon-btn" onClick={onClose} disabled={!!freshToken || busy} type="button" title={freshToken ? "Save your token first" : "Close"}>
            <Icon name="x" size={14} />
          </button>
        </header>

        <div className="settings-submodal-body">
          {freshToken && (
            <div className="settings-recovery">
              <div className="settings-recovery-head">
                <Icon name="key" size={14} />
                <strong>Save this token — it won't be shown again</strong>
              </div>
              <p className="settings-paragraph">
                Use this in place of a password for scripts, the Android app, or any
                long-lived client. Treat it like a password — anyone with it can act as you.
              </p>
              <code className="settings-mono-block settings-token-block">{freshToken.token}</code>
              <div className="settings-button-row">
                <button className="btn" onClick={copyFresh} type="button">
                  <Icon name="check" size={13} /> Copy
                </button>
                <button className="btn btn-primary" onClick={() => setFreshToken(null)} type="button">
                  I've saved it
                </button>
              </div>
            </div>
          )}

          {!freshToken && (
            <>
              <p className="settings-paragraph">
                Long-lived credentials for scripts and the Android app. Each token can be
                revoked individually if it ever leaks.
              </p>

              <div className="settings-row">
                <label className="settings-row-label">New token name</label>
                <div className="settings-button-row">
                  <input
                    className="input"
                    placeholder="e.g. iPhone, GitHub Actions"
                    value={newName}
                    onInput={(e) => setNewName((e.currentTarget as HTMLInputElement).value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); issue(); } }}
                    disabled={busy}
                    style={{ flex: 1 }}
                  />
                  <button
                    className="btn btn-primary"
                    onClick={issue}
                    disabled={busy || !newName.trim()}
                    type="button"
                  >
                    <Icon name="plus" size={13} /> Issue
                  </button>
                </div>
              </div>

              <div className="settings-row">
                <label className="settings-row-label">Active tokens ({tokens?.length ?? 0})</label>
                {tokens === null ? (
                  <p className="settings-loading">Loading…</p>
                ) : tokens.length === 0 ? (
                  <p className="settings-empty">No tokens yet.</p>
                ) : (
                  <ul className="settings-list">
                    {tokens.map((t) => (
                      <li key={t.id} className="settings-list-row">
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div className="settings-list-row-title">{t.name}</div>
                          <div className="settings-list-row-meta">
                            Created {fmtRel(t.createdAt)}
                            {t.lastUsedAt ? ` · last used ${fmtRel(t.lastUsedAt)}` : " · never used"}
                            {t.expiresAt ? ` · expires ${fmtRel(t.expiresAt)}` : ""}
                          </div>
                        </div>
                        <button
                          className="btn settings-danger-btn"
                          onClick={() => revoke(t)}
                          type="button"
                        >
                          <Icon name="x" size={12} /> Revoke
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// Same shape as the formatRelative we use elsewhere — kept local so
// this modal stays self-contained.
function fmtRel(iso: string): string {
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return "—";
  const diff = Date.now() - t;
  const sec = Math.floor(Math.abs(diff) / 1000);
  const future = diff < 0;
  const fmt = (n: number, unit: string) =>
    future ? `in ${n}${unit}` : `${n}${unit} ago`;
  if (sec < 60) return future ? "soon" : "just now";
  const min = Math.floor(sec / 60);
  if (min < 60) return fmt(min, "m");
  const hr = Math.floor(min / 60);
  if (hr < 24) return fmt(hr, "h");
  const day = Math.floor(hr / 24);
  if (day < 7) return fmt(day, "d");
  if (day < 30) return fmt(Math.floor(day / 7), "w");
  return fmt(Math.floor(day / 30), "mo");
}
